import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { FiMoreVertical } from 'react-icons/fi';
import { NavigateText } from '../../../components/navigate-text/NavigateText';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import { Column } from '../../../components/resource-grid/ResourceGrid';
import { InsurancePlan } from '../../../types/plans/types';
import {
  rangeToLabel,
  reconstructAgeRange,
} from '../../../utils/ageRanges';
import { getStatusConfig } from '../../../utils/getStatusConfig';
import { PLAN_DETAIL_COPY } from './planDetailCopy';

export const createStaticColumns = (
  duplicateProviders: Record<string, string> = {},
  canEditInsurance: () => boolean = () => false,
  isAdmin: () => boolean = () => false
): Column<InsurancePlan>[] => [
  {
    header: PLAN_DETAIL_COPY.providerLabels.provider,
    accessor: 'provider',
    isCustomRender: true,
    width: '13.75rem',
    render: (row: InsurancePlan) => {
      const fullName = `${row.provider.firstName || ''} ${
        row.provider.middleName || ''
      } ${row.provider.lastName || ''} ${
        row.provider.suffix || ''
      }`.trim();

      return (
        <Tooltip
          label={fullName}
          placement="top"
          hasArrow
          isDisabled={fullName.length <= 20}
        >
          <Box
            maxW="100%"
            aria-label={`${PLAN_DETAIL_COPY.a11y.providerRow}: ${fullName}`}
          >
            <ProviderName
              provider={{
                id: row.provider.id || '',
                firstname: row.provider.firstName || '',
                lastname: row.provider.lastName || '',
                middlename: row.provider.middleName || null,
                suffix: row.provider.suffix || null,
                customFields: {},
              }}
              duplicateProviders={duplicateProviders}
              mode="table"
              size="sm"
            />
          </Box>
        </Tooltip>
      );
    },
  },
  {
    header: PLAN_DETAIL_COPY.providerLabels.practice,
    accessor: 'practice',
    isCustomRender: true,
    width: '16.25rem',
    render: (row: InsurancePlan) => (
      <Tooltip
        label={row.practice.name || ''}
        placement="top"
        hasArrow
        isDisabled={(row.practice.name || '').length <= 25}
      >
        <Box maxW="100%">
          <NavigateText
            name={row.practice.name || ''}
            id={row.practice.id || ''}
            resource="practice"
          />
        </Box>
      </Tooltip>
    ),
  },
  {
    header: PLAN_DETAIL_COPY.providerLabels.state,
    accessor: 'practice',
    isCustomRender: true,
    width: '4.125rem',
    render: (row: InsurancePlan) => <Text>{row.practice.state}</Text>,
  },
  {
    header: PLAN_DETAIL_COPY.providerLabels.market,
    accessor: 'marketName',
    width: '14.25rem',
  },
  {
    header: PLAN_DETAIL_COPY.providerLabels.credentialStatus,
    accessor: 'plans',
    isCustomRender: true,
    render: (row: InsurancePlan) => {
      const p = row.plans[0];
      // Get credential status and value
      const credentialStatus = p.credentialstatus || 'Credentialed';
      const value = p.value || 'No Status';

      // Get status config
      let statusConfig;
      if (credentialStatus?.toLowerCase() === 'pending') {
        statusConfig = getStatusConfig('No Status', p.insurance, undefined, 'Pending');
      } else {
        statusConfig = getStatusConfig(value, p.insurance, undefined, credentialStatus, p.metadata);
      }

      return (
        <Tooltip
          label={statusConfig.helperText}
          hasArrow
          placement="top-start"
          maxW="25rem"
          minW="15rem"
          offset={[0, 10]}
        >
          <Box
            display="flex"
            height="3.5rem"
            padding="1rem"
            justifyContent="center"
            alignItems="center"
            gap="0.25rem"
            width="100%"
            maxWidth="12rem"
            borderRadius="0.3125rem"
            background={statusConfig.bgColor}
            cursor="help"
            transition="all 0.2s ease-in-out"
            flexDirection="column"
            flexShrink={0}
            overflow="hidden"
            position="relative"
            data-testid="chip"
          >
            <Text
              fontSize="0.75rem"
              fontWeight="600"
              lineHeight="1.1"
              color="white"
              textAlign="left"
              whiteSpace="normal"
              wordBreak="break-word"
              width="100%"
              maxW="100%"
              flex="0 0 auto"
            >
              {statusConfig.label}
            </Text>
            <Text
              fontSize="0.625rem"
              fontWeight="400"
              lineHeight="1.0"
              color="white"
              textAlign="left"
              opacity="0.9"
              whiteSpace="normal"
              wordBreak="break-word"
              width="100%"
              maxW="100%"
              flex="0 0 auto"
            >
              {statusConfig.helperText.split(' | ')[0]}
            </Text>
          </Box>
        </Tooltip>
      );
    },
    width: '14.25rem',
  },
  {
    header: PLAN_DETAIL_COPY.providerLabels.ageRange,
    accessor: 'plans',
    isCustomRender: true,
    render: (row: InsurancePlan) => {
      const p = row.plans[0];
      // Use reconstructAgeRange for consistent age range handling
      const ageRange = reconstructAgeRange({
        minimumAge: p.minimumAge,
        maximumAge: p.maximumAge,
        age: p.age,
        metadata: p.metadata as
          | {
              minimumAge?: number | null;
              maximumAge?: number | null;
            }
          | undefined,
      });
      const label = rangeToLabel(ageRange);
      return <Text whiteSpace="normal">{label}</Text>;
    },
    width: '14.25rem',
  },
  {
    header: '',
    accessor: 'actions',
    isCustomRender: true,
    width: '4rem',
    render: (row: InsurancePlan) =>
      canEditInsurance() || isAdmin() ? (
        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton}
            aria-label="Actions"
            icon={<FiMoreVertical />}
            variant="ghost"
            size="sm"
          />
          <MenuList>
            <MenuItem
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('edit-provider-credential-status', {
                    detail: {
                      providerId: row.provider.id,
                      providerName: `${
                        row.provider.firstName || ''
                      } ${row.provider.lastName || ''} ${
                        row.provider.suffix || ''
                      }`.trim(),
                    },
                  })
                )
              }
            >
              Edit Provider Credential Status for{' '}
              {row.provider.firstName || ''}{' '}
              {row.provider.lastName || ''}{' '}
              {row.provider.suffix || ''}
            </MenuItem>
            {/* <MenuItem
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent('go-to-bulk-edit', {
                  detail: {
                    providerId: row.provider.id,
                    practiceId: row.practice.id,
                  },
                })
              )
            }
          >
            Bulk edit insurance row
          </MenuItem> */}
          </MenuList>
        </Menu>
      ) : (
        <Box width="4rem" height="2rem" />
      ),
  },
];

// Backward compatibility export - uses empty duplicateProviders
export const staticColumns: Column<InsurancePlan>[] =
  createStaticColumns({});
